"use client";

import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { mergeClasses } from "@huub-web/utils";
import { AnimatePresence, motion } from "motion/react";
import {
  Button,
  Flex,
  Icon,
  IconButton,
  Logo,
  Portal,
  useKeys,
} from "@huub-web/ui";
import { useNavigationState } from "../../providers";
import type { ThemeName } from "@huub-web/ui/theme/types";
import { transitionDurations } from "@huub-web/ui/theme/transitionDurations";
import { elevations } from "@huub-web/ui/theme/css/elevations.css";
import { backdropStyle } from "@huub-web/ui/components/Backdrop/Backdrop.css";

import { NavigationMenu } from "./NavigationMenu";

import {
  closeButtonStyle,
  containerStyle,
  headerStyle,
  menuBackdropStyle,
} from "./MobileNavigation.css";

type MobileNavigationProps = {
  /**
   * Called when a navigation item is clicked
   */
  onNavigate?: (key: string) => void;
  /**
   * Called when the user logs out
   */
  onLogout?: () => void;
  /**
   * Whether navigation items are disabled
   */
  disabled?: boolean;
  /**
   * Active theme name
   */
  theme?: ThemeName;
};

/**
 * Mobile navigation rendered as a portal overlay with backdrop.
 * Uses AnimatePresence for enter/exit animations.
 */
export const MobileNavigation: FC<MobileNavigationProps> = ({
  disabled = false,
  onNavigate,
  onLogout,
  theme,
}) => {
  const { t } = useTranslation();
  const { isMenuOpen, setMenuOpen } = useNavigationState();

  useKeys(["Escape"], () => {
    setMenuOpen(false);
  });

  const handleNavigationChange = (key: string) => {
    onNavigate?.(key);
    setMenuOpen(false);
  };

  const handleBackdropClick = () => {
    setMenuOpen(false);
  };

  return (
    <Portal>
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              className={mergeClasses(backdropStyle(), menuBackdropStyle)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: transitionDurations.s,
                ease: "easeInOut",
              }}
              onClick={handleBackdropClick}
            />

            <motion.div
              className={mergeClasses(containerStyle, elevations.l)}
              initial={{ opacity: 0, scale: 0.9, transformOrigin: "top left" }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{
                duration: transitionDurations.s,
                ease: "easeInOut",
              }}
            >
              <Flex direction="column">
                <Flex className={headerStyle}>
                  <IconButton
                    icon="close"
                    size="s"
                    rounded
                    onClick={() => setMenuOpen(false)}
                    className={closeButtonStyle}
                    border="default"
                    variant="opaque"
                  />
                  <Logo theme={theme} />
                </Flex>
                <NavigationMenu
                  variant="full"
                  hideLogo
                  onNavigationChange={handleNavigationChange}
                  disabled={disabled}
                  theme={theme}
                  trailing={
                    onLogout && (
                      <Button
                        variant="secondary"
                        size="s"
                        leading={<Icon icon="logOut" size="s" />}
                        onClick={onLogout}
                      >
                        {t("auth.logout.button")}
                      </Button>
                    )
                  }
                />
              </Flex>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </Portal>
  );
};
